import {
  Content,
  Header,
  HeaderLabel,
  Page,
  InfoCard,
  LinkButton,
} from '@backstage/core-components';
import {
  Grid,
  Typography,
  makeStyles,
  Chip,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CodeIcon from '@material-ui/icons/Code';
import DnsIcon from '@material-ui/icons/Dns';
import LockIcon from '@material-ui/icons/Lock';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import StorageIcon from '@material-ui/icons/Storage';
import LanguageIcon from '@material-ui/icons/Language';
import GetAppIcon from '@material-ui/icons/GetApp';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PersonIcon from '@material-ui/icons/Person';
import BuildIcon from '@material-ui/icons/Build';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import GitHubIcon from '@material-ui/icons/GitHub';
import LayersIcon from '@material-ui/icons/Layers';
import SecurityIcon from '@material-ui/icons/Security';
import AppsIcon from '@material-ui/icons/Apps';
import CloudIcon from '@material-ui/icons/Cloud';
import FolderIcon from '@material-ui/icons/Folder';
import UpdateIcon from '@material-ui/icons/Update';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const useStyles = makeStyles(theme => ({
  heroSection: {
    background:
      'linear-gradient(135deg, color-mix(in srgb, var(--ui-primary) 10%, transparent) 0%, color-mix(in srgb, var(--ui-primary) 4%, transparent) 100%)',
    borderRadius: 24,
    padding: theme.spacing(5, 6),
    border: '1px solid var(--ui-border)',
    marginBottom: theme.spacing(4),
    position: 'relative' as const,
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -60,
      right: -60,
      width: 200,
      height: 200,
      background:
        'radial-gradient(circle, color-mix(in srgb, var(--ui-primary) 14%, transparent) 0%, transparent 70%)',
      borderRadius: '50%',
    },
  },
  heroTitle: {
    fontWeight: 700,
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
    color: 'var(--ui-text)',
  },
  heroSubtitle: {
    fontSize: '1.1rem',
    color: 'var(--ui-text-muted)',
    maxWidth: 700,
    lineHeight: 1.6,
  },
  tagRow: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap' as const,
  },
  tag: {
    backgroundColor: 'var(--ui-bg-muted)',
    color: 'var(--ui-text)',
    borderColor: 'var(--ui-primary)',
    fontWeight: 500,
  },
  // The card is a flex column, so its content fills the height on its own.
  // Stretching every child div to 100% stretched the header along with it.
  card: {
    height: '100%',
  },
  featureIcon: {
    color: 'var(--ui-primary)',
    minWidth: 44,
  },
  // List items do not wrap by default, which cut longer descriptions off at
  // the card edge.
  featureText: {
    whiteSpace: 'normal',
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: 'var(--ui-primary)',
    // On the violet circle, so white is correct here — the one place on this
    // page where it is.
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.875rem',
    minWidth: 32,
    marginRight: theme.spacing(2),
  },
  stepItem: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  archDiagram: {
    // Was a dark slab holding light text; the text follows the theme now, so
    // the slab has to as well or the diagram is dark-on-dark in light mode.
    background: 'var(--ui-bg-muted)',
    borderRadius: 16,
    padding: theme.spacing(4),
    border: '1px solid var(--ui-border)',
    fontFamily: 'monospace',
    textAlign: 'center' as const,
  },
  archRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  archBox: {
    background: 'var(--ui-bg-muted)',
    border: '1px solid var(--ui-border)',
    borderRadius: 12,
    padding: theme.spacing(1.5, 3),
    color: 'var(--ui-text)',
    fontWeight: 600,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  archBoxInfra: {
    background:
      'color-mix(in srgb, var(--color-brand-secondary) 12%, transparent)',
    border:
      '1px solid color-mix(in srgb, var(--color-brand-secondary) 35%, transparent)',
    borderRadius: 12,
    padding: theme.spacing(1.5, 3),
    color: 'var(--ui-primary)',
    fontWeight: 600,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  archArrow: {
    color: 'var(--docs-card-muted)',
    textAlign: 'center' as const,
    marginBottom: theme.spacing(2),
    fontSize: '1.2rem',
  },
  codeBlock: {
    background: 'var(--ui-bg-muted)',
    border: '1px solid var(--ui-border)',
    borderRadius: 12,
    padding: theme.spacing(2, 3),
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: 'var(--ui-text)',
    overflowX: 'auto' as const,
    '& b': {
      color: 'var(--ui-primary)',
    },
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  prerequisiteItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  linkRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    marginTop: theme.spacing(2),
  },
  infraLabel: {
    color: 'var(--ui-text-muted)',
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: 1.5,
    fontWeight: 600,
    textAlign: 'center' as const,
    marginBottom: theme.spacing(1),
  },
}));

export const JamPage = () => {
  const classes = useStyles();

  return (
    <Page themeId="tool">
      <Header
        title="revenexx JAM"
        subtitle="The Ultimate Local Developer Runtime for Cloud-Native Workflows"
      >
        {/* No version here: a hard-coded one went stale with every release.
            The download page always shows the current one. */}
        <HeaderLabel
          label="Download"
          value="jam.revenexx.com"
          url="https://jam.revenexx.com"
        />
        <HeaderLabel label="Platform" value="macOS · Windows · Linux" />
        <HeaderLabel label="Stack" value="Tauri · React · Rust" />
      </Header>
      <Content>
        {/* Hero Section */}
        <div className={classes.heroSection}>
          <Typography className={classes.heroTitle}>
            Your Local Cloud, One Click Away
          </Typography>
          <Typography className={classes.heroSubtitle}>
            JAM bridges the gap between your workstation and the revenexx Cloud.
            It provides a production-grade infrastructure stack inside your
            local Docker environment — Traefik reverse proxy, CoreDNS, and a
            built-in Certificate Authority — so you can develop at{' '}
            <code>*.rvnxx.test</code> with zero-config networking.
          </Typography>
          <div className={classes.tagRow}>
            <Chip
              label="Rust"
              variant="outlined"
              size="small"
              className={classes.tag}
            />
            <Chip
              label="Tauri 2"
              variant="outlined"
              size="small"
              className={classes.tag}
            />
            <Chip
              label="React 19"
              variant="outlined"
              size="small"
              className={classes.tag}
            />
            <Chip
              label="TypeScript"
              variant="outlined"
              size="small"
              className={classes.tag}
            />
            <Chip
              label="Docker"
              variant="outlined"
              size="small"
              className={classes.tag}
            />
            <Chip
              label="Tailwind CSS"
              variant="outlined"
              size="small"
              className={classes.tag}
            />
          </div>
          <div className={classes.linkRow}>
            <LinkButton
              to="https://github.com/revenexx/jam"
              color="primary"
              variant="contained"
              startIcon={<GitHubIcon />}
            >
              Source Code
            </LinkButton>
            <LinkButton
              to="https://jam.revenexx.com"
              color="default"
              variant="outlined"
              startIcon={<GetAppIcon />}
            >
              Download
            </LinkButton>
            <LinkButton
              to="/docs/default/component/jam"
              color="default"
              variant="outlined"
              startIcon={<AppsIcon />}
            >
              Full Documentation
            </LinkButton>
          </div>
        </div>

        <Grid container spacing={3}>
          {/* Core Features */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Core Features" className={classes.card}>
              <List dense>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <PlayArrowIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Instant Microservices"
                    secondary="Clone and spin up services from the central catalog in seconds."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <SettingsEthernetIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Edge Gateway"
                    secondary="Built-in Traefik v3 reverse proxy for local https://<slug>.rvnxx.test routing."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <DnsIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Seamless DNS"
                    secondary="Automated wildcard DNS via CoreDNS — no more /etc/hosts hacking."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <LockIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Identity-First"
                    secondary="Native OIDC/OAuth2 PKCE sign-in with your revenexx ID."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <VpnKeyIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Secrets from Infisical"
                    secondary="Fills each service's .env from secrets.revenexx.com before it starts — never overwrites values you set yourself."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <CodeIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Developer-Centric"
                    secondary="Automated VS Code integration, terminal logs, and real-time status monitoring."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Trusted HTTPS"
                    secondary="Automatic SSL certificate issuance for local domains via an internal CA."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <UpdateIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Automatic Updates"
                    secondary="Checks for a new version at launch and installs it with one click."
                  />
                </ListItem>
              </List>
            </InfoCard>
          </Grid>

          {/* Architecture Overview */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Architecture" className={classes.card}>
              <Typography
                variant="body2"
                style={{ marginBottom: 16, color: 'var(--ui-text-muted)' }}
              >
                JAM follows a "Desktop as a Control Plane" philosophy. The app
                orchestrates local infrastructure while your services run in
                Docker.
              </Typography>
              <div className={classes.archDiagram}>
                <div className={classes.infraLabel}>App Layer</div>
                <div className={classes.archRow}>
                  <div className={classes.archBox}>
                    <DesktopWindowsIcon fontSize="small" /> React UI
                  </div>
                  <div className={classes.archBox}>
                    <BuildIcon fontSize="small" /> Tauri IPC
                  </div>
                  <div className={classes.archBox}>
                    <StorageIcon fontSize="small" /> Rust Core
                  </div>
                </div>
                <div className={classes.archArrow}>
                  ↓ Docker API &amp; Git/SSH ↓
                </div>
                <div className={classes.infraLabel}>Infrastructure Layer</div>
                <div className={classes.archRow}>
                  <div className={classes.archBoxInfra}>
                    <SettingsEthernetIcon fontSize="small" /> Traefik
                  </div>
                  <div className={classes.archBoxInfra}>
                    <DnsIcon fontSize="small" /> CoreDNS
                  </div>
                  <div className={classes.archBoxInfra}>
                    <SecurityIcon fontSize="small" /> Local CA
                  </div>
                </div>
                <div className={classes.archArrow}>↓ Routes Traffic To ↓</div>
                <div className={classes.infraLabel}>Workloads</div>
                <div className={classes.archRow}>
                  <div className={classes.archBox}>
                    <CloudIcon fontSize="small" /> Your Microservices
                  </div>
                </div>
              </div>
            </InfoCard>
          </Grid>

          {/* Getting Started */}
          <Grid item xs={12} md={8}>
            <InfoCard title="Getting Started">
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 16 }}
              >
                From zero to your first locally running service in 5 minutes.
              </Typography>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>1</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Install Prerequisites
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Make sure Docker is running, Git is installed, and you can
                    sign in with your revenexx ID.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>2</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Download &amp; Launch JAM
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Sign in at{' '}
                    <a
                      href="https://jam.revenexx.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--ui-primary)' }}
                    >
                      jam.revenexx.com
                    </a>{' '}
                    with your revenexx ID and download the installer for your
                    platform: macOS, Windows (<code>.exe</code> or{' '}
                    <code>.msi</code>) or Linux (AppImage, <code>.deb</code>,{' '}
                    <code>.rpm</code> or Arch package, x86_64 or ARM). The macOS
                    and Windows installers are code-signed, and the macOS build
                    is notarized. On macOS, on Windows and with the AppImage you
                    only do this once: JAM updates itself afterwards.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>3</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Sign In with revenexx ID
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    On first launch, click{' '}
                    <strong>"Sign In with revenexx ID"</strong>. Your browser
                    opens the revenexx ID login (<code>id.revenexx.com</code>) —
                    sign in with your company account.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>4</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Start the Gateway
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Navigate to the <strong>Dashboard</strong> and click{' '}
                    <strong>"Start Gateway"</strong>. JAM pulls the Traefik and
                    CoreDNS images and serves a locally generated certificate
                    for <code>*.rvnxx.test</code>. Optionally click{' '}
                    <strong>"Auto-Configure"</strong> under DNS Routing to set
                    up OS-level DNS (asks for your password once), and{' '}
                    <strong>"Trust CA"</strong> so your browser accepts the
                    certificate.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>5</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Clone Your First Service
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Go to the <strong>Catalog</strong> tab, browse available
                    microservices, and click <strong>"Clone Code"</strong>. The
                    service appears in your Services tab, ready to run.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start">
                <div className={classes.stepNumber}>6</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Run &amp; Develop
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Hit <strong>Play</strong> on your service. Once the green
                    indicator appears, open it in your browser at{' '}
                    <code>https://&lt;slug&gt;.rvnxx.test</code> or launch VS
                    Code directly.
                  </Typography>
                </Box>
              </Box>
            </InfoCard>
          </Grid>

          {/* Prerequisites & Quick Info */}
          <Grid item xs={12} md={4}>
            <InfoCard title="Prerequisites">
              <div className={classes.prerequisiteItem}>
                <CheckCircleIcon
                  style={{ color: 'var(--ui-primary)' }}
                  fontSize="small"
                />
                <Typography variant="body2">
                  <strong>Docker Desktop</strong>, or Docker Engine + Compose v2
                  on Linux (must be running)
                </Typography>
              </div>
              <div className={classes.prerequisiteItem}>
                <CheckCircleIcon
                  style={{ color: 'var(--ui-primary)' }}
                  fontSize="small"
                />
                <Typography variant="body2">
                  <strong>Git</strong> installed
                </Typography>
              </div>
              <div className={classes.prerequisiteItem}>
                <CheckCircleIcon
                  style={{ color: 'var(--ui-primary)' }}
                  fontSize="small"
                />
                <Typography variant="body2">
                  <strong>revenexx ID</strong>, your company sign-in at{' '}
                  <a
                    href="https://id.revenexx.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--ui-primary)' }}
                  >
                    id.revenexx.com
                  </a>
                </Typography>
              </div>
              <div className={classes.prerequisiteItem}>
                <CheckCircleIcon
                  style={{ color: 'var(--ui-primary)' }}
                  fontSize="small"
                />
                <Typography variant="body2">
                  <strong>Your password</strong> once, for the DNS setup
                </Typography>
              </div>
              <div className={classes.prerequisiteItem}>
                <CheckCircleIcon
                  style={{ color: 'var(--ui-primary)' }}
                  fontSize="small"
                />
                <Typography variant="body2">
                  <strong>macOS, Windows, or Linux</strong>
                </Typography>
              </div>
            </InfoCard>

            <Box mt={3}>
              <InfoCard title="Gateway Stack">
                <Typography
                  variant="body2"
                  style={{ color: 'var(--ui-text-muted)', marginBottom: 12 }}
                >
                  The Gateway provisions two local containers and a local
                  certificate:
                </Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    <SettingsEthernetIcon
                      fontSize="inherit"
                      style={{ verticalAlign: 'middle', marginRight: 6 }}
                    />
                    Traefik v3
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: 'var(--ui-text-muted)',
                      fontSize: '0.8rem',
                    }}
                  >
                    Reverse proxy on ports 80/443. Auto-discovers containers
                    with traefik labels.
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    <DnsIcon
                      fontSize="inherit"
                      style={{ verticalAlign: 'middle', marginRight: 6 }}
                    />
                    CoreDNS
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: 'var(--ui-text-muted)',
                      fontSize: '0.8rem',
                    }}
                  >
                    Resolves *.rvnxx.test → 127.0.0.1. No /etc/hosts editing
                    needed.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    <SecurityIcon
                      fontSize="inherit"
                      style={{ verticalAlign: 'middle', marginRight: 6 }}
                    />
                    Local CA
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: 'var(--ui-text-muted)',
                      fontSize: '0.8rem',
                    }}
                  >
                    JAM generates a root CA and a *.rvnxx.test wildcard
                    certificate on your machine and adds the CA to the OS trust
                    store. Traefik serves the certificate directly.
                  </Typography>
                </Box>
              </InfoCard>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <InfoCard title="Make Your Service JAM-Ready">
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 16 }}
              >
                To integrate any service into the JAM ecosystem, add the
                following to your service's <code>docker-compose.yml</code>:
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginBottom: 8 }}
                  >
                    1. Add Traefik Labels
                  </Typography>
                  <div className={classes.codeBlock}>
                    <pre style={{ margin: 0 }}>
                      {`services:
  my-service:
    build: .
    labels:
      - "traefik.enable=true"
      - "traefik.http.routers.my-service.rule=Host(\`my-service.rvnxx.test\`)"
      - "traefik.http.routers.my-service.entrypoints=websecure"
      - "traefik.http.routers.my-service.tls=true"
    networks:
      - rvnxx-gateway`}
                    </pre>
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginBottom: 8 }}
                  >
                    2. Join the Gateway Network
                  </Typography>
                  <div className={classes.codeBlock}>
                    <pre style={{ margin: 0 }}>
                      {`networks:
  rvnxx-gateway:
    external: true`}
                    </pre>
                  </div>
                  <Typography
                    variant="body2"
                    style={{
                      color: 'var(--ui-text-muted)',
                      marginTop: 12,
                      fontSize: '0.85rem',
                    }}
                  >
                    This tells Docker to connect your service to the shared
                    gateway network. Once running, Traefik auto-discovers and
                    routes traffic to your container at{' '}
                    <code>https://my-service.rvnxx.test</code>.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginBottom: 8 }}
                  >
                    3. Register It in the Catalog
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)', marginBottom: 12 }}
                  >
                    JAM's catalog lists what the revenexx Console finds. Add a{' '}
                    <code>.rxmeta/manifest.json</code> to your repo so the
                    service shows up there and others can clone it with one
                    click. Its category decides where JAM puts the code in the
                    workspace.
                  </Typography>
                  <LinkButton
                    to="/docs/default/component/jam/onboarding/#part-2-registering-in-the-service-catalog-console"
                    color="default"
                    variant="outlined"
                    startIcon={<AppsIcon />}
                  >
                    Onboarding Guide
                  </LinkButton>
                </Grid>
              </Grid>
            </InfoCard>
          </Grid>

          {/* Running next to the Constructor proxy (DX-697) */}
          <Grid item xs={12}>
            <InfoCard title="Running Next to Constructor">
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 16 }}
              >
                JAM and the Constructor proxy both want ports 80 and 443. To use
                both at the same time, JAM keeps those ports and passes the
                Constructor hosts on to the Constructor proxy. Your Constructor
                projects keep their URLs and certificates.
              </Typography>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>1</div>
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Move the Constructor proxy to other ports
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)', marginBottom: 8 }}
                  >
                    In <code>~/.project-infrastructure/.env</code>, set:
                  </Typography>
                  <div
                    className={classes.codeBlock}
                    style={{ display: 'inline-block' }}
                  >
                    <pre style={{ margin: 0 }}>
                      {`DOCKER_HTTP_=8081
DOCKER_HTTPS=8444`}
                    </pre>
                  </div>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)', marginTop: 8 }}
                  >
                    Then restart it with <code>constructor infra:restart</code>.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" mb={3}>
                <div className={classes.stepNumber}>2</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Turn on the setting in JAM
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Open{' '}
                    <strong>Settings → Security &amp; Infrastructure</strong>{' '}
                    and switch on{' '}
                    <strong>Constructor Proxy Compatibility</strong>. If the
                    gateway is running, JAM asks to restart it. Hosts ending in{' '}
                    <code>.local</code> or <code>.internal</code> now reach
                    Constructor.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="flex-start" mb={2}>
                <div className={classes.stepNumber}>3</div>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Add other project domains
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: 'var(--ui-text-muted)' }}
                  >
                    Only for projects on another domain, such as{' '}
                    <code>dev-shop.nord.com</code>: list it under{' '}
                    <strong>Extra Constructor hostnames</strong> and click{' '}
                    <strong>Save Hostnames</strong>. Its subdomains are
                    included.
                  </Typography>
                </Box>
              </Box>

              <LinkButton
                to="/docs/default/component/jam/userguide/constructor-proxy/"
                color="default"
                variant="outlined"
                startIcon={<AppsIcon />}
              >
                Details &amp; Rolling Back
              </LinkButton>
            </InfoCard>
          </Grid>

          {/* Everyday use */}
          <Grid item xs={12} md={6}>
            <InfoCard
              title="Workspace, Secrets & Updates"
              className={classes.card}
            >
              <Box mb={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  <FolderIcon
                    fontSize="inherit"
                    style={{ verticalAlign: 'middle', marginRight: 6 }}
                  />
                  Where your code lives
                </Typography>
                <Typography
                  variant="body2"
                  style={{ color: 'var(--ui-text-muted)', fontSize: '0.8rem' }}
                >
                  JAM clones into <code>~/rvnxx</code>, sorted by category:{' '}
                  <code>services/&lt;slug&gt;</code>,{' '}
                  <code>apps/&lt;slug&gt;</code> and{' '}
                  <code>components/&lt;group&gt;/&lt;slug&gt;</code>. Pick
                  another folder under <strong>Settings → Workspace</strong>;
                  JAM offers to move existing services. It can be your existing
                  projects folder: JAM only looks into these three folders.
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  <VpnKeyIcon
                    fontSize="inherit"
                    style={{ verticalAlign: 'middle', marginRight: 6 }}
                  />
                  Secrets
                </Typography>
                <Typography
                  variant="body2"
                  style={{ color: 'var(--ui-text-muted)', fontSize: '0.8rem' }}
                >
                  Enter your Infisical machine identity under{' '}
                  <strong>Settings → Secrets Management</strong>. On every start
                  JAM fills placeholders and missing keys in the service's{' '}
                  <code>.env</code> from <code>secrets.revenexx.com</code>.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  <UpdateIcon
                    fontSize="inherit"
                    style={{ verticalAlign: 'middle', marginRight: 6 }}
                  />
                  Updates
                </Typography>
                <Typography
                  variant="body2"
                  style={{ color: 'var(--ui-text-muted)', fontSize: '0.8rem' }}
                >
                  JAM checks for a new version at every launch and shows a
                  banner to install it and relaunch. To check by hand, open{' '}
                  <strong>Settings → About &amp; Updates</strong>.
                </Typography>
              </Box>
            </InfoCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <InfoCard title="When Something Breaks" className={classes.card}>
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 12 }}
              >
                Start with the <strong>Diagnostics</strong> page (the pulse icon
                at the top right). It checks Docker, the gateway, DNS and every
                local service.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <HelpOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="The gateway does not start"
                    secondary="Usually another proxy holds port 80 or 443 — often the Constructor proxy, see Running Next to Constructor above."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <HelpOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="*.rvnxx.test does not resolve"
                    secondary="Run Auto-Configure under DNS Routing on the Dashboard again."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <HelpOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="The browser warns about the certificate"
                    secondary="Click Trust CA on the Dashboard, then restart the browser."
                  />
                </ListItem>
              </List>
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 12 }}
              >
                Still stuck? Ask <strong>Elmar Hepp</strong>, who looks after
                JAM, on Microsoft Teams.
              </Typography>
              <LinkButton
                to="/docs/default/component/jam/userguide/troubleshooting/"
                color="default"
                variant="outlined"
                startIcon={<AppsIcon />}
              >
                Troubleshooting Guide
              </LinkButton>
            </InfoCard>
          </Grid>

          {/* Development & Contributing */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Local Development">
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 12 }}
              >
                Want to contribute to JAM itself? You need Node.js 18+, Rust via{' '}
                <code>rustup</code> and, on Linux, the GTK/WebKit packages
                listed in the{' '}
                <a
                  href="/docs/default/component/jam/userguide/contributing/"
                  style={{ color: 'var(--ui-primary)' }}
                >
                  contributing guide
                </a>
                . Then clone the repo and run:
              </Typography>
              <div className={classes.codeBlock}>
                <pre style={{ margin: 0 }}>
                  {`# Clone the repository
git clone https://github.com/revenexx/jam.git
cd jam

# Install dependencies (the root workspace includes ui/)
npm install

# Run in development mode
npm run dev

# Build for production
npm run build`}
                </pre>
              </div>
              <Typography
                variant="body2"
                style={{
                  color: 'var(--ui-text-muted)',
                  marginTop: 12,
                  fontSize: '0.85rem',
                }}
              >
                The frontend runs on <code>localhost:5173</code> via Vite. The
                Rust backend compiles automatically through the Tauri CLI.
              </Typography>
            </InfoCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <InfoCard title="Security & Identity">
              <Typography
                variant="body2"
                style={{ color: 'var(--ui-text-muted)', marginBottom: 12 }}
              >
                JAM uses <strong>OAuth2 with PKCE</strong> for secure,
                browser-based authentication against the revenexx Identity
                Provider.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <LockIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Tokens stored securely"
                    secondary="Credentials are stored in the system keyring (Keychain on macOS, Credential Manager on Windows, Secret Service on Linux)."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <SecurityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="No credentials in config files"
                    secondary="Sign-in tokens, the Infisical credentials and the GitHub token live only in the keyring, never in JAM's config files."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon className={classes.featureIcon}>
                    <LanguageIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.featureText}
                    primary="Local HTTPS everywhere"
                    secondary="A CA generated on your machine issues the *.rvnxx.test certificate. All local traffic is encrypted."
                  />
                </ListItem>
              </List>
            </InfoCard>
          </Grid>

          {/* Links & Resources */}
          <Grid item xs={12}>
            <InfoCard title="Quick Links">
              <Grid container spacing={2}>
                <Grid item>
                  <LinkButton
                    to="https://github.com/revenexx/jam"
                    color="primary"
                    variant="outlined"
                    startIcon={<GitHubIcon />}
                  >
                    GitHub Repository
                  </LinkButton>
                </Grid>
                <Grid item>
                  <LinkButton
                    to="https://jam.revenexx.com"
                    color="primary"
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                  >
                    Download
                  </LinkButton>
                </Grid>
                <Grid item>
                  <LinkButton
                    to="https://github.com/revenexx/jam/releases"
                    color="primary"
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                  >
                    GitHub Releases
                  </LinkButton>
                </Grid>
                <Grid item>
                  <LinkButton
                    to="/docs/default/component/jam"
                    color="primary"
                    variant="outlined"
                    startIcon={<AppsIcon />}
                  >
                    Full TechDocs
                  </LinkButton>
                </Grid>
                <Grid item>
                  <LinkButton
                    to="/catalog/default/component/jam"
                    color="primary"
                    variant="outlined"
                    startIcon={<LayersIcon />}
                  >
                    Catalog Entry
                  </LinkButton>
                </Grid>
                <Grid item>
                  <LinkButton
                    to="/docs/default/component/jam/operations-website/"
                    color="primary"
                    variant="outlined"
                    startIcon={<BuildIcon />}
                  >
                    Operating jam.revenexx.com
                  </LinkButton>
                </Grid>
                <Grid item>
                  <LinkButton
                    to="https://console.revenexx.com"
                    color="primary"
                    variant="outlined"
                    startIcon={<PersonIcon />}
                  >
                    revenexx Console
                  </LinkButton>
                </Grid>
              </Grid>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
